<template>
  <div class="ulp">
    <!-- Remove confirmation prompt -->
    <popup
        v-if="showRemovePrompt"
        @close="showRemovePrompt = false"
    >
      <delete-prompt
          slot="popupMainContent"
          @abort="showRemovePrompt = false"
          @confirm="() => { showRemovePrompt = false; removeLicense(); }"
      />
    </popup>

    <!-- Loading -->
    <div v-if="loading" class="ulp__loading">
      <loading-spinner />
    </div>

    <!-- User has a license -->
    <div v-else-if="userLicense" class="ulp__card">
      <div class="ulp__card-header">
        <span class="ulp__license-name">{{ userLicense.name }}</span>
        <span :class="['ulp__type-badge', 'ulp__type-badge--' + userLicense.type]">
          {{ $t(userLicense.type) || userLicense.type }}
        </span>
        <button
            class="ulp__remove-btn"
            :disabled="removing"
            :title="$t('removeLicense')"
            @click="showRemovePrompt = true"
        >
          {{ removing ? '…' : '✕' }}
        </button>
      </div>

      <div class="ulp__meta">
        <div class="ulp__meta-item">
          <span class="ulp__meta-label">{{ $t('state') }}</span>
          <span :class="['ulp__state', 'ulp__state--' + userLicense.state]">{{ userLicense.state }}</span>
        </div>
        <div class="ulp__meta-item">
          <span class="ulp__meta-label">{{ $t('startsAt') }}</span>
          <span>{{ userLicense.startDate || '—' }}</span>
        </div>
        <div class="ulp__meta-item">
          <span class="ulp__meta-label">{{ $t('expiresAt') }}</span>
          <span>{{ userLicense.expirationDate || '—' }}</span>
        </div>
        <div class="ulp__meta-item">
          <span class="ulp__meta-label">{{ $t('licenseType') }}</span>
          <span>{{ userLicense.type }}</span>
        </div>
      </div>
    </div>

    <!-- No license: assign UI -->
    <div v-else class="ulp__assign">
      <div class="ulp__availability">
        <span class="ulp__availability-count">{{ unassignedLicenses.length }} / {{ orgLicenses.length }}</span>
        <span class="ulp__availability-label">{{ $t('licensesAvailable') }}</span>
      </div>

      <div v-if="unassignedLicenses.length > 0" class="ulp__assign-controls">
        <select v-model="selectedLicenseId" class="custom-select dark ulp__select">
          <option value="">{{ $t('selectLicense') }}</option>
          <option
              v-for="lic in unassignedLicenses"
              :key="lic.id"
              :value="lic.id"
          >
            {{ lic.name }}
            ({{ lic.type }}{{ lic.expirationDate ? ', ' + $t('expiresAt') + ': ' + lic.expirationDate : '' }})
          </option>
        </select>

        <button
            :disabled="!selectedLicenseId || assigning"
            class="ulp__assign-btn"
            @click="assignLicense"
        >
          {{ assigning ? $t('assigning') : $t('assign') }}
        </button>
      </div>

      <div v-else class="ulp__empty">
        {{ $t('noLicensesAvailable') }}
      </div>
    </div>

    <div v-if="assignError" class="ulp__error">{{ assignError }}</div>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import Popup from '@/components/Popup.vue';
import DeletePrompt from '@/components/forms/DeletePrompt.vue';

export default {
  name: 'UserLicensePanel',

  components: {
    LoadingSpinner,
    Popup,
    DeletePrompt
  },

  props: {
    userId: {
      type: String,
      required: true
    },
    homeOrganizationId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: true,
      userLicense: null,
      orgLicenses: [],
      selectedLicenseId: '',
      assigning: false,
      removing: false,
      showRemovePrompt: false,
      assignError: ''
    };
  },

  computed: {
    unassignedLicenses() {
      return this.orgLicenses.filter(l => !l.userId);
    }
  },

  watch: {
    userId() {
      this.load();
    },
    homeOrganizationId() {
      this.load();
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.userLicense = null;
      this.orgLicenses = [];
      this.selectedLicenseId = '';
      this.assignError = '';

      try {
        // Load this user's license
        const userLicenses = await this.$store.dispatch('clientLoadLicenses', {
          filter: `userId eq ${this.userId}`
        });

        if (userLicenses && userLicenses.length > 0) {
          this.userLicense = userLicenses[0];
        } else if (this.homeOrganizationId) {
          // No license assigned — load org pool
          this.orgLicenses = await this.$store.dispatch('clientLoadLicenses', {
            filter: `organizationId eq ${this.homeOrganizationId}`
          }) || [];
        }
      } finally {
        this.loading = false;
      }
    },

    async assignLicense() {
      if (!this.selectedLicenseId) return;
      this.assigning = true;
      this.assignError = '';
      try {
        await this.$store.dispatch('clientUpdateLicense', {
          id: this.selectedLicenseId,
          userId: this.userId
        });
        await this.load();
      } catch (e) {
        this.assignError = e && e.message ? e.message : String(e);
      } finally {
        this.assigning = false;
      }
    },

    async removeLicense() {
      if (!this.userLicense) return;
      this.removing = true;
      this.assignError = '';
      try {
        await this.$store.dispatch('clientUpdateLicense', {
          id: this.userLicense.id,
          userId: null
        });
        await this.load();
      } catch (e) {
        this.assignError = e && e.message ? e.message : String(e);
      } finally {
        this.removing = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.ulp {
  margin-top: 16px;
}

.ulp__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

/* License card (user has a license) */
.ulp__card {
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}

.ulp__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.ulp__license-name {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
}

.ulp__type-badge {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(255, 255, 255, 0.1);

  &--admin    { background: rgba(220, 53, 69, 0.2);  color: #ff8080; }
  &--manager  { background: rgba(255, 193, 7, 0.2);  color: #ffc107; }
  &--editor   { background: rgba(0, 123, 255, 0.2);  color: #74b4ff; }
  &--viewer   { background: rgba(40, 167, 69, 0.2);  color: #6fcf97; }
}

.ulp__remove-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid rgba(235, 87, 87, 0.35);
  border-radius: 6px;
  background: rgba(235, 87, 87, 0.1);
  color: #ff8080;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) { background: rgba(235, 87, 87, 0.25); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.ulp__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 24px;
}

.ulp__meta-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 13px;
}

.ulp__meta-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.45);
}

.ulp__state {
  font-weight: 600;
  &--active   { color: #6fcf97; }
  &--inactive { color: rgba(255, 255, 255, 0.5); }
  &--expired  { color: #ff8080; }
  &--revoked  { color: #ff8080; }
}

/* Assign UI (no license) */
.ulp__assign {
  padding: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
}

.ulp__availability {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
}

.ulp__availability-count {
  font-size: 20px;
  font-weight: 700;
}

.ulp__availability-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}

.ulp__assign-controls {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.ulp__select {
  flex: 1;
  min-width: 180px;
}

.ulp__assign-btn {
  min-width: 90px;
  min-height: 38px;
  padding: 8px 18px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: #ffffff;
  color: #222222;
  font: inherit;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.ulp__empty {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
}

.ulp__error {
  margin-top: 10px;
  color: #ff6b6b;
  font-size: 13px;
}
</style>

